/**
 * In-app contact form.
 *
 * Trimmed version of the marketing /contact: name + email come from
 * the session and are never asked. Just a message textarea + Send.
 * Posts to /api/conversations; the same handler the marketing form
 * uses, with no Turnstile because auth is the gate.
 *
 * Attachments land in slice 2.
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle, Button } from '@bookaburra/ui';
import { CheckCircle2, Paperclip, X } from 'lucide-react';
import { AppHeader } from '../components/AppHeader';
import { authClient } from '../auth/client';
import { useDocumentTitle } from '../lib/use-document-title';

const MAX_FILES = 5;
const MAX_PER_FILE = 10 * 1024 * 1024;
const MAX_TOTAL = 20 * 1024 * 1024;

export const Route = createFileRoute('/contact')({
  component: ContactPage,
});

function ContactPage() {
  useDocumentTitle('Contact');
  const { data: session, isPending } = authClient.useSession();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function addFiles(picked: FileList | File[]) {
    const next = [...files];
    let total = next.reduce((a, f) => a + f.size, 0);
    for (const f of Array.from(picked)) {
      if (next.length >= MAX_FILES) {
        setError(`Up to ${MAX_FILES} files.`);
        break;
      }
      if (f.size > MAX_PER_FILE) {
        setError(`${f.name} is bigger than 10 MB.`);
        continue;
      }
      if (total + f.size > MAX_TOTAL) {
        setError('Total size over 20 MB.');
        break;
      }
      next.push(f);
      total += f.size;
    }
    setFiles(next);
  }

  function removeFile(i: number) {
    setFiles((f) => f.filter((_, idx) => idx !== i));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const trimmed = message.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError(null);
    try {
      const form = new FormData();
      form.set(
        'payload',
        JSON.stringify({
          message: trimmed,
          sourcePage: window.location.pathname,
        }),
      );
      for (const f of files) form.append('attachments', f);
      const res = await fetch('/api/conversations', {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error || `Send failed (${res.status})`);
        setSubmitting(false);
        return;
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Send failed');
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader />
      <main className="mx-auto max-w-xl px-6 py-12 sm:py-16">
        <header className="text-center">
          <h1 className="text-3xl font-bold tracking-tight">Contact support</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Tim reads every message. Bookaburra is a side project, so
            replies aren&rsquo;t same-day — but they do come.
          </p>
        </header>

        {isPending ? (
          <p className="mt-10 text-center text-sm text-muted-foreground">
            Loading&hellip;
          </p>
        ) : !session?.user ? (
          <NotSignedIn />
        ) : done ? (
          <Alert variant="success" className="mt-10">
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>Sent</AlertTitle>
            <AlertDescription>
              Thanks for getting in touch. Bookaburra is a side project, so
              replies may be slow. I&rsquo;ll follow up when I can.{' '}
              <Link to="/" className="underline-offset-4 hover:underline">
                Back to home
              </Link>
              .
            </AlertDescription>
          </Alert>
        ) : (
          <form
            onSubmit={onSubmit}
            className="mt-10 space-y-5 rounded-2xl border border-brand-sage/15 bg-card p-6 shadow-brand"
          >
            <div className="rounded-md bg-secondary/50 px-3 py-2 text-xs text-muted-foreground">
              Signing this as <span className="font-medium">{session.user.email}</span>.
            </div>
            <label className="block">
              <span className="text-sm font-medium">Message</span>
              <textarea
                rows={8}
                required
                autoFocus
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={10_000}
                placeholder="What's going on?"
                className="mt-1.5 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </label>

            <div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  if (e.target.files) addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium hover:bg-secondary"
              >
                <Paperclip className="h-3.5 w-3.5" />
                Attach files
              </button>
              {files.length > 0 ? (
                <ul className="mt-2 space-y-1">
                  {files.map((f, i) => (
                    <li
                      key={`${f.name}-${i}`}
                      className="flex items-center justify-between rounded-md bg-secondary/40 px-2 py-1 text-xs"
                    >
                      <span className="truncate">
                        {f.name}{' '}
                        <span className="text-muted-foreground">
                          ({formatBytes(f.size)})
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        aria-label={`Remove ${f.name}`}
                        className="ml-2 text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            {error ? (
              <p className="text-xs text-destructive">{error}</p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate({ to: '/' })}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !message.trim()}>
                {submitting ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function NotSignedIn() {
  return (
    <Alert className="mt-10">
      <AlertTitle>Sign in first</AlertTitle>
      <AlertDescription>
        Sign in so your message is attached to your account.{' '}
        <Link to="/signin" className="underline-offset-4 hover:underline">
          Sign in
        </Link>{' '}
        or{' '}
        <a
          href="https://bookaburra.com/contact"
          className="underline-offset-4 hover:underline"
        >
          use the public form
        </a>
        .
      </AlertDescription>
    </Alert>
  );
}
